/**
 * Owner Portal · Notifications — every owner-lifecycle mail CoLiviq has sent
 * this owner, reconstructed from the *_owner_email_sent_at columns (see
 * useOwnerNotifications), with local-only read state and a property filter.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, ChevronDown } from "lucide-react";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { EmptyState } from "@/components/admin/v2/primitives/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOwnerNotifications } from "@/hooks/v2/useOwnerNotifications";
import { OWNER_NOTIFICATION_ICONS } from "@/lib/owner-notification-icons";
import { useLanguage } from "@/contexts/LanguageContext";
import { ownerNotificationCopy } from "@/lib/owner-notification-copy";
import type { OwnerNotificationRow } from "@/hooks/v2/useOwnerNotifications";

function timeAgo(
  iso: string,
  t: (key: string, fallback: string) => string
): string {
  const ms = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return t("owner.notifications.justNow", "Just now");
  if (hours < 24)
    return `${hours}${t("owner.notifications.hoursAgoSuffix", "h ago")}`;
  const days = Math.floor(hours / 24);
  if (days === 1) return t("owner.notifications.yesterday", "Yesterday");
  if (days < 7)
    return `${days}${t("owner.notifications.daysAgoSuffix", "d ago")}`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

export default function OwnerNotifications() {
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const copy = (row: OwnerNotificationRow) =>
    ownerNotificationCopy(row, t, locale);
  const {
    rows,
    properties,
    unreadCount,
    isLoading,
    isError,
    error,
    refetch,
    markRead,
    markAllRead,
  } = useOwnerNotifications();
  const [propertyFilter, setPropertyFilter] = useState<string | "all">("all");

  const filteredRows = useMemo(
    () =>
      propertyFilter === "all"
        ? rows
        : rows.filter((r) => r.property === propertyFilter),
    [rows, propertyFilter]
  );
  const filteredUnread = filteredRows.reduce(
    (n, r) => (r.unread ? n + 1 : n),
    0
  );

  return (
    <AdminV2Layout
      title={t("owner.notifications.title", "Notifications")}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin?tab=settings&section=notifications">
              {t(
                "owner.notifications.managePreferences",
                "Manage email preferences"
              )}
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={markAllRead}
            disabled={unreadCount === 0}
          >
            {t("owner.notifications.markAllRead", "Mark all as read")}
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-3 mb-5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="gap-2 rounded-full border-border/70 bg-card pl-4 pr-3 shadow-sm hover:bg-muted/50"
            >
              {propertyFilter === "all"
                ? t("owner.notifications.allProperties", "All properties")
                : propertyFilter}
              {filteredUnread > 0 ? (
                <Badge className="rounded-full bg-primary/10 px-2 py-0 text-xs font-semibold text-primary hover:bg-primary/10">
                  {filteredUnread} {t("owner.notifications.unread", "unread")}
                </Badge>
              ) : null}
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="rounded-xl">
            <DropdownMenuItem onClick={() => setPropertyFilter("all")}>
              {t("owner.notifications.allProperties", "All properties")}
            </DropdownMenuItem>
            {properties.map((p) => (
              <DropdownMenuItem key={p} onClick={() => setPropertyFilter(p)}>
                {p}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
        </div>
      ) : isError ? (
        <Card className="p-4 border-destructive bg-destructive/5">
          <p className="text-sm font-medium">
            {t(
              "owner.notifications.errorTitle",
              "Notifications could not be loaded"
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {error instanceof Error ? error.message : "Unknown error"}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => refetch()}
          >
            {t("owner.notifications.retry", "Try again")}
          </Button>
        </Card>
      ) : filteredRows.length === 0 ? (
        <EmptyState
          icon={Bell}
          title={t("owner.notifications.emptyTitle", "No notifications yet")}
          description={t(
            "owner.notifications.emptyDesc",
            "You'll see an update here the moment something happens on your properties."
          )}
        />
      ) : (
        <div className="space-y-3">
          {filteredRows.map((row) => {
            const Icon = OWNER_NOTIFICATION_ICONS[row.kind];
            return (
              <div
                key={row.id}
                className={
                  "grid grid-cols-[auto_1fr_auto] items-start gap-4 rounded-2xl border bg-card p-4 sm:p-5 shadow-sm transition-all hover:shadow-md" +
                  (row.unread
                    ? " border-l-4 border-l-primary border-y-border/60 border-r-border/60 bg-primary/[0.04]"
                    : " border-border/60 hover:border-border")
                }
              >
                <div
                  className={
                    "h-10 w-10 rounded-xl grid place-items-center shrink-0" +
                    (row.unread
                      ? " bg-primary text-primary-foreground shadow-sm"
                      : " bg-muted text-muted-foreground")
                  }
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  {/* The copy is resolved HERE, not in the feed builder: it
                      used to be built as German sentences inside the builder,
                      which is why the EN switch translated every label around
                      this list and nothing in it. */}
                  <span className="font-semibold text-sm sm:text-[15px]">
                    {copy(row).title}
                  </span>
                  <p className="text-sm text-muted-foreground mt-0.5 mb-3 leading-relaxed">
                    {copy(row).desc}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant="outline"
                      className="rounded-full font-normal text-muted-foreground"
                    >
                      {row.property}
                    </Badge>
                    {row.unread ? (
                      <button
                        type="button"
                        onClick={() => markRead(row.id)}
                        className="inline-flex h-7 items-center gap-1 rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 active:scale-[0.98]"
                      >
                        <Check className="h-3 w-3" />
                        {t("owner.notifications.markRead", "Mark as read")}
                      </button>
                    ) : null}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap pt-1">
                  {timeAgo(row.ts, t)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </AdminV2Layout>
  );
}
